import * as React from 'react';

import type { IntersectionChartItem } from 'types';

type Props = {
  item1: IntersectionChartItem,
  item2: IntersectionChartItem,
  overlap: IntersectionChartItem,
  maxSize: number,
  minSize: number
};

type State = {
  item1Size: number,
  item2Size: number,
  item1Overlap: number,
  item2Overlap: number
};

type Styles = {
  background?: string,
  width?: string,
  height?: string,
  left?: string,
  right?: string,
  transform?: string
};

function inPixel(maxSize) {
  return `${maxSize}px`;
}

class IntersectionChart extends React.PureComponent<Props, State> {
  static defaultProps = {
    maxSize: 200,
    minSize: 30
  };

  timeoutId?: number;

  state = {
    item1Size: 0,
    item2Size: 0,
    item1Overlap: 0,
    item2Overlap: 0
  };

  componentDidMount() {
    // This allows transitions on loading the chart by setting values after the dom has been painted once
    this.timeoutId = window.setTimeout(this.updateLayoutCalculations);
  }

  componentDidUpdate(prevProps: Props) {
    const props = this.props;
    if (
      props.maxSize !== prevProps.maxSize ||
      props.minSize !== prevProps.minSize ||
      props.item1.value !== prevProps.item1.value ||
      props.item2.value !== prevProps.item2.value ||
      props.overlap.value !== prevProps.overlap.value
    ) {
      this.updateLayoutCalculations();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timeoutId);
  }

  getSmallerItemSize(smallerValue: number, biggerValue: number): number {
    const { minSize, maxSize } = this.props;
    return Math.max(minSize, maxSize * (smallerValue / biggerValue));
  }

  getSizes(): {
    item1Size: number,
    item2Size: number
    } {
    const { maxSize, item1, item2 } = this.props;
    let item1Size;
    let item2Size;

    if (!item1.value && !item2.value) {
      return {
        item1Size: 0,
        item2Size: 0
      };
    }

    if (item1.value > item2.value) {
      item1Size = maxSize;
      item2Size = this.getSmallerItemSize(item2.value, item1.value);
    } else {
      item2Size = maxSize;
      item1Size = this.getSmallerItemSize(item1.value, item2.value);
    }

    return {
      item1Size,
      item2Size
    };
  }

  getOverlapFraction(value: number): number {
    const { overlap } = this.props;
    return (overlap.value / value);
  }

  updateLayoutCalculations = () => {
    const { item1: { value: item1Value }, item2: { value: item2Value } } = this.props;
    const { item1Size, item2Size } = this.getSizes();
    let item1Overlap = 0;
    let item2Overlap = 0;

    if (item1Value < item2Value) {
      item1Overlap = this.getOverlapFraction(item1Value);
    } else {
      item2Overlap = this.getOverlapFraction(item2Value);
    }

    this.setState({ item1Size, item2Size, item1Overlap, item2Overlap });
  };

  getBubbleStyles(item: 'item1' | 'item2'): Styles {
    const { [item]: { color } } = this.props;
    const itemSize = this.state[`${item}Size`];
    const pixelItemSize = inPixel(itemSize);
    const itemOverlapFraction = this.state[`${item}Overlap`];
    const positioning = item === 'item1' ? 'right' : 'left';
    const pixelItemOverlap = inPixel(itemSize * itemOverlapFraction);

    return {
      background: color,
      width: pixelItemSize,
      height: pixelItemSize,
      right: positioning === 'right' ? `calc(50% - ${pixelItemOverlap})` : 'auto',
      left: positioning === 'left' ? `calc(50% - ${pixelItemOverlap})` : 'auto'
    };
  }

  getPlotAreaStyles(): Styles {
    const { maxSize, item1, item2 } = this.props;
    const totalWidth = 2 * maxSize;
    const { item2Size, item1Size, item1Overlap, item2Overlap } = this.state;

    const styles = {
      height: inPixel(maxSize),
      width: inPixel(totalWidth)
    };

    if (!item1Size && !item2Size) {
      return styles;
    }

    const overlap = item1Overlap ? item1Size * item1Overlap : item2Size * item2Overlap;
    const usedSpace = item1Size + item2Size - overlap;
    const widthOffset = (totalWidth - usedSpace) / 2;
    const direction = item1.value >= item2.value ? 1 : -1;

    return {
      ...styles,
      transform: `translateX(${direction * widthOffset}px)`
    };
  }

  renderLegend(item: IntersectionChartItem): React.ReactNode {
    if (!item.legend) {
      return null;
    }

    return (
      <div className="legend">
        <span
          style={{ backgroundColor: item.legend.color }}
          className="legend__icon"
        />
        {item.legend.formatter(item)}
      </div>
    );
  }

  render(): React.ReactNode {
    const { maxSize, item1, item2, overlap } = this.props;
    return (
      <div className="intersection-chart" style={{ height: inPixel(maxSize) }}>
        <div className="intersection-chart__legends">
          {this.renderLegend(item1)}
          {this.renderLegend(overlap)}
          {this.renderLegend(item2)}
        </div>
        <div className="intersection-chart__plot-area" style={this.getPlotAreaStyles()}>
          <div className="intersection-chart__bubble" style={this.getBubbleStyles('item1')} />
          <div className="intersection-chart__bubble" style={this.getBubbleStyles('item2')} />
        </div>
      </div>
    );
  }
}

export default IntersectionChart;
